import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useEffect, useMemo } from "react";
import type { PropsWithChildren } from "react";
import { toast, Toaster } from "sonner";
import { AuthProvider } from "@/features/auth/AuthProvider";
import { env } from "@/shared/lib/env";
import { flushOfflineQueue } from "@/shared/offline/syncEngine";
import { useAuth } from "@/features/auth/AuthProvider";
import { applyTheme, loadThemePreference, type ThemePreference } from "@/shared/theme";

function ThemeController({ children }: PropsWithChildren) {
  const { tailor } = useAuth();

  useEffect(() => {
    const settings = tailor?.settings_json;
    const remoteSettings = settings && typeof settings === "object" && !Array.isArray(settings) ? settings as Record<string, unknown> : null;
    const preference = loadThemePreference(remoteSettings);
    applyTheme(preference);
    let activePreference = preference;

    const media = typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : null;
    const handleSystemChange = () => {
      if (activePreference === "system") applyTheme("system");
    };
    const handlePreferenceChange = (event: Event) => {
      const preferenceEvent = event as CustomEvent<ThemePreference>;
      activePreference = preferenceEvent.detail ?? "system";
      applyTheme(activePreference);
    };
    media?.addEventListener?.("change", handleSystemChange);
    window.addEventListener("tailorbook:theme-change", handlePreferenceChange);
    return () => {
      media?.removeEventListener?.("change", handleSystemChange);
      window.removeEventListener("tailorbook:theme-change", handlePreferenceChange);
    };
  }, [tailor]);

  return <>{children}</>;
}

function initMonitoring() {
  if (env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: env.VITE_SENTRY_DSN,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1
    });
  }

  if (env.VITE_POSTHOG_KEY) {
    posthog.init(env.VITE_POSTHOG_KEY, {
      api_host: env.VITE_POSTHOG_HOST,
      autocapture: false,
      capture_pageview: false
    });
  }
}

export function AppProviders({ children }: PropsWithChildren) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false
          },
          mutations: {
            retry: 0
          }
        }
      }),
    []
  );

  useEffect(() => {
    initMonitoring();
  }, []);

  // Replay offline-queued mutations on startup and whenever connectivity returns.
  useEffect(() => {
    const flush = () => {
      void flushOfflineQueue().then((result) => {
        if (result.synced > 0) {
          toast.success(`${result.synced} offline ${result.synced === 1 ? "change" : "changes"} synced`);
          void queryClient.invalidateQueries();
        }
        if (result.dropped > 0) {
          toast.error(`${result.dropped} offline ${result.dropped === 1 ? "change" : "changes"} could not be saved`);
        }
      });
    };

    flush();
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider><ThemeController>{children}</ThemeController></AuthProvider>
      <Toaster richColors position="top-center" />
    </QueryClientProvider>
  );
}
